import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCurrentToken } from '../../Access/services/authService';
import {
  filterReferencePlaceKindsForSurface,
  REFERENCE_PLACE_SURFACE_SHARE,
} from '../data/referencePlaceKinds';
import { referencePlaceKindLabels } from '../utils/referencePlaces';
import { useReferencePlacesCatalog } from './useReferencePlacesCatalog';
import { useReferencePlacesHover } from './useReferencePlacesHover';
import { useReferencePlacesLayer } from './useReferencePlacesLayer';
import { useReferencePlacesTilesLayer } from './useReferencePlacesTilesLayer';
import { useReferencePlacesViewport } from './useReferencePlacesViewport';

/**
 * Orquesta catálogo, switcher y pines.
 * /app y share con JWT: PMTiles (`GET /getpmtilesurl`).
 * Share anónimo (sin JWT): GET por caja. Si la firma de tiles falla, el share
 * cae al GET; /app no (no martillar PostGIS).
 *
 * No montar en CAD canvas / image.
 *
 * @param {object} options
 * @param {React.MutableRefObject} options.mapInstanceRef
 * @param {boolean} [options.enabled] switcher visible (mismo momento que
 *   MapControls). El catálogo se pide al montar, no espera `mapReady`.
 * @param {boolean} [options.interactive] false = se abre el switcher pero no
 *   pinta ni deja prender kinds (invitado). Default true.
 * @param {boolean} [options.preferTiles] true = solo tiles; false = solo caja;
 *   omitido = tiles si hay JWT.
 * @param {string} [options.surface] `app` = catálogo completo; `share` =
 *   amenities (sin seguridad). Default share.
 */
export function useShareReferencePlaces({
  mapInstanceRef,
  enabled = false,
  interactive = true,
  preferTiles = null,
  surface = REFERENCE_PLACE_SURFACE_SHARE,
} = {}) {
  const { kinds: catalogKinds, loading: catalogLoading } = useReferencePlacesCatalog({
    enabled: true,
  });
  const kinds = useMemo(
    () => filterReferencePlaceKindsForSurface(catalogKinds, surface),
    [catalogKinds, surface],
  );
  const [enabledKinds, setEnabledKinds] = useState([]);
  const [mapAttachVersion, setMapAttachVersion] = useState(0);
  const [tilesUnavailable, setTilesUnavailable] = useState(false);
  const hasJwt = Boolean(getCurrentToken());
  const wantTiles = preferTiles === true || (preferTiles !== false && hasJwt);
  const allowBboxFallback = preferTiles !== true;

  useEffect(() => {
    if (!enabled) return undefined;

    if (mapInstanceRef.current) {
      setMapAttachVersion((version) => version + 1);
      return undefined;
    }

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (mapInstanceRef.current) {
        setMapAttachVersion((version) => version + 1);
        window.clearInterval(intervalId);
        return;
      }
      if (attempts >= 120) window.clearInterval(intervalId);
    }, 50);

    return () => window.clearInterval(intervalId);
  }, [enabled, mapInstanceRef]);

  useEffect(() => {
    if (enabled && interactive) return;
    setEnabledKinds([]);
    setTilesUnavailable(false);
  }, [enabled, interactive]);

  const kindSet = useMemo(() => new Set(kinds.map((entry) => entry.kind)), [kinds]);

  useEffect(() => {
    setEnabledKinds((current) => {
      const next = current.filter((kind) => kindSet.has(kind));
      if (next.length === current.length && next.every((kind, index) => kind === current[index])) {
        return current;
      }
      return next;
    });
  }, [kindSet]);

  const toggleKind = useCallback((kind) => {
    if (!interactive) return;
    const key = String(kind || '').trim();
    if (!key) return;
    setEnabledKinds((current) => {
      if (current.includes(key)) return current.filter((entry) => entry !== key);
      if (!kindSet.has(key)) return current;
      return [...current, key];
    });
  }, [interactive, kindSet]);

  const kindLabels = useMemo(() => referencePlaceKindLabels(kinds), [kinds]);
  const layerEnabled = enabled && interactive && enabledKinds.length > 0;

  const tilesLayerEnabled = layerEnabled && wantTiles && !tilesUnavailable;

  const { failed: tilesFailed } = useReferencePlacesTilesLayer({
    mapInstanceRef,
    enabled: tilesLayerEnabled,
    kinds: enabledKinds,
  });

  useEffect(() => {
    if (tilesFailed && allowBboxFallback) {
      // El hook de tiles limpia al deshabilitarse; forzamos el flag para
      // pasar a GET por caja sin dejar un source vectorial huérfano encima.
      setTilesUnavailable(true);
    }
  }, [allowBboxFallback, tilesFailed]);

  const useBbox = layerEnabled && (!wantTiles || tilesUnavailable);

  const { places, truncated, viewportTooLarge } = useReferencePlacesViewport({
    mapInstanceRef,
    enabled: useBbox,
    kinds: enabledKinds,
    mapAttachVersion,
  });

  useReferencePlacesLayer({
    mapInstanceRef,
    places,
    enabled: useBbox,
    kindLabels,
  });

  useReferencePlacesHover({
    mapInstanceRef,
    enabled: layerEnabled,
    kindLabels,
  });

  const viewportHint = !layerEnabled
    ? ''
    : useBbox && viewportTooLarge
      ? 'Acercá el mapa para ver los puntos de interés'
      : useBbox && truncated
        ? 'Hay más puntos de interés: acercá el mapa'
        : '';

  return {
    kinds,
    enabledKinds,
    toggleKind,
    truncated,
    viewportTooLarge,
    mapControlProps: {
      showReferencePlacesToggle: enabled && (catalogLoading || kinds.length > 0),
      referencePlacesLoading: enabled && catalogLoading,
      referencePlacesLocked: enabled && !interactive,
      referencePlaceKinds: kinds,
      enabledReferencePlaceKinds: interactive ? enabledKinds : [],
      onToggleReferencePlaceKind: toggleKind,
      referencePlacesViewportHint: viewportHint,
    },
  };
}
